import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Zap, Link, Shield, AlertCircle, FastForward, Cpu, CheckCircle2 } from 'lucide-react';
import { SOVEREIGN_CONSTANTS } from '../constants';
import { NodeService } from '../services/nodeService';

export const SuperluminalHandshake: React.FC = () => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [complete, setComplete] = useState(NodeService.isPhaseLocked());

  const igniteHandshake = () => {
    setIsSyncing(true);
    setComplete(false);
    setLogs([]);

    const sequence = [
      { t: 0, m: "--- INITIATING PHASE-LOCK SYNC ---" },
      { t: 800, m: "Accessing Dark Substrate Path..." },
      { t: 1500, m: `Bypassing Light Speed: Velocity ${SOVEREIGN_CONSTANTS.SUPERLUMINAL_VELOCITY} m/s` },
      { t: 2200, m: `Aligning Phase to ${SOVEREIGN_CONSTANTS.TARGET_NODE}` },
      { t: 3000, m: `SYNC COMPLETE: Node is now one with ${SOVEREIGN_CONSTANTS.TARGET_NODE}.` },
      { t: 3500, m: `Latency: ${SOVEREIGN_CONSTANTS.LATENCY_PHASE}c (Pre-Perception Arrival).` },
      { t: 4000, m: "VERDICT: The 5 Pillars are now a Single Star-Core." },
      { t: 4500, m: "SOVEREIGN LINK ESTABLISHED." }
    ];

    sequence.forEach((item, i) => {
      setTimeout(() => {
        setLogs(prev => [...prev, item.m]);
        if (i === sequence.length - 1) {
          setIsSyncing(false);
          setComplete(true);
          NodeService.setPhaseLock(true);
        }
      }, item.t);
    });
  };

  return (
    <div className="glass-panel p-6 bg-black/60 border-lattice-cyan/10 relative overflow-hidden">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${complete ? 'bg-lattice-cyan/20' : 'bg-white/5'}`}>
            <FastForward className={`w-5 h-5 ${complete ? 'text-lattice-cyan' : 'text-white/20'}`} />
          </div>
          <div>
            <h3 className="text-sm font-display font-bold text-white uppercase tracking-widest">Superluminal Handshake</h3>
            <p className="text-[9px] font-mono text-white/40 uppercase">Phase-Lock Sync | 22.48x c</p>
          </div>
        </div>
        
        <button 
          onClick={igniteHandshake}
          disabled={isSyncing}
          className={`px-4 py-1.5 rounded-full text-[9px] font-mono font-bold uppercase transition-all
            ${isSyncing 
              ? 'bg-lattice-cyan/10 text-lattice-cyan cursor-wait' 
              : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'}`}
        >
          {isSyncing ? 'Syncing...' : 'Ignite Phase-Lock'}
        </button>
      </div>

      <div className="space-y-4">
        <div className="bg-black border border-white/5 rounded-xl p-4 font-mono text-[9px] min-h-[140px] relative overflow-hidden">
           {complete && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.05 }}
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
              >
                 <Shield className="w-32 h-32 text-lattice-cyan" />
              </motion.div>
           )}
           
           <div className="relative z-10">
              <AnimatePresence>
                {logs.map((log, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, x: -5 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={log.includes('VERDICT') || log.includes('COMPLETE') ? 'text-lattice-cyan font-bold pt-1' : 'text-white/60'}
                  >
                    <span className="text-white/20 mr-2">[{i}]</span>
                    {log}
                  </motion.div>
                ))}
              </AnimatePresence>
           </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
           <div className="p-2 bg-white/5 border border-white/5 rounded-lg">
              <p className="text-[7px] text-white/20 uppercase mb-1">Velocity</p>
              <p className="text-[10px] font-mono text-white font-bold">22.48c</p>
           </div>
           <div className="p-2 bg-white/5 border border-white/5 rounded-lg">
              <p className="text-[7px] text-white/20 uppercase mb-1">Latency</p>
              <p className="text-[10px] font-mono text-lattice-cyan font-bold">-21.48c</p>
           </div>
        </div>

        {complete && (
           <motion.div 
             initial={{ opacity: 0, y: 10 }}
             animate={{ opacity: 1, y: 0 }}
             className="flex items-center gap-2 px-3 py-2 bg-lattice-cyan/5 border border-lattice-cyan/20 rounded-lg"
           >
              <CheckCircle2 className="w-3 h-3 text-lattice-cyan" />
              <p className="text-[8px] font-mono text-white/60">NODE IS NOW ONE WITH THE LATTICE ROOT</p>
           </motion.div>
        )}
      </div>
    </div>
  );
};
